import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/lib/auth';
import { CareAndGoalsCard } from '@/components/participants/care-plan/CareAndGoalsCard';
import {
  MessageCircle, Accessibility, AlertTriangle, MapPin, Calendar, Pencil,
  FileText, Activity, Shield, Target, Users, Globe, Heart, UtensilsCrossed,
  DollarSign, Clock, ChevronDown, ArrowRight, User, Camera
} from 'lucide-react';
import { getBrandName } from '@/lib/brandName';

/* ── Snapshot Hero ── */
function SnapshotHero({ snapshot, onReview }) {
  if (!snapshot) return null;
  const { mobility, communication, alerts, assistance_required, address_summary, next_schedule, meta, photo_compliance } = snapshot;
  const activeAlerts = (alerts || []).filter(a => a.active !== false);

  return (
    <div className="border rounded-xl bg-emerald-50/40 p-5" data-testid="v3-snapshot-hero">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-full bg-emerald-100 flex items-center justify-center">
            <Shield className="w-4 h-4 text-emerald-700" />
          </div>
          <span className="text-sm font-bold uppercase tracking-wide text-foreground">Support Snapshot</span>
          {meta?.updated_at && (
            <span className="text-xs text-muted-foreground ml-2">
              Last reviewed: {new Date(meta.updated_at).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' })}
              {meta.updated_by_name && ` by ${meta.updated_by_name}`}
            </span>
          )}
        </div>
        {onReview && (
          <Button variant="outline" size="sm" className="h-8 text-xs" onClick={onReview} data-testid="v3-snapshot-review-btn">Review</Button>
        )}
      </div>

      {/* 2-row grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-4">
        {/* Row 1 */}
        <div className="flex items-start gap-3">
          <MessageCircle className="w-5 h-5 text-muted-foreground mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm font-semibold">Communication</p>
            <p className="text-xs text-muted-foreground capitalize">{communication?.type || 'Verbal'}</p>
            {communication?.notes && <p className="text-xs text-muted-foreground">{communication.notes}</p>}
          </div>
        </div>
        <div className="flex items-start gap-3">
          <Users className="w-5 h-5 text-muted-foreground mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm font-semibold">Assistance Required</p>
            {assistance_required?.length > 0 ? (
              <div className="text-xs text-muted-foreground">
                <p className="font-medium text-foreground">
                  {assistance_required[0]?.level === 'full' ? 'High' : assistance_required[0]?.level === 'partial' ? 'Medium' : 'Low'}
                  {' — '}{assistance_required.map(a => a.category).join(', ')}
                </p>
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">None recorded</p>
            )}
          </div>
        </div>
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
          <div>
            <div className="flex items-center gap-1.5">
              <p className="text-sm font-semibold">Alerts</p>
              {activeAlerts.length > 0 && <Badge variant="destructive" className="text-[10px] px-1.5 py-0">{activeAlerts.length} Active</Badge>}
            </div>
            {activeAlerts.length > 0 ? (
              activeAlerts.slice(0, 2).map((a, i) => (
                <p key={i} className="text-xs text-muted-foreground">{a.type}: {a.severity}</p>
              ))
            ) : (
              <p className="text-xs text-muted-foreground">No active alerts</p>
            )}
          </div>
        </div>
        <div className="flex items-start gap-3">
          <MapPin className="w-5 h-5 text-muted-foreground mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm font-semibold">Address</p>
            <p className="text-xs text-muted-foreground">{address_summary || 'Not recorded'}</p>
          </div>
        </div>

        {/* Row 2 */}
        <div className="flex items-start gap-3">
          <Accessibility className="w-5 h-5 text-muted-foreground mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm font-semibold">Mobility</p>
            <p className="text-xs text-muted-foreground">{mobility?.wheelchair ? 'Wheelchair' : 'Independent'}</p>
            {mobility?.notes && <p className="text-xs text-muted-foreground">{mobility.notes}</p>}
          </div>
        </div>
        <div className="flex items-start gap-3">
          <UtensilsCrossed className="w-5 h-5 text-muted-foreground mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm font-semibold">Diet</p>
            <p className="text-xs text-muted-foreground">Regular diet</p>
            <p className="text-xs text-muted-foreground">No specific requirements</p>
          </div>
        </div>
        <div className="flex items-start gap-3">
          <Heart className="w-5 h-5 text-muted-foreground mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm font-semibold">Other Considerations</p>
            <p className="text-xs text-muted-foreground">{snapshot.quick_notes || 'No additional notes'}</p>
          </div>
        </div>
        <div className="flex items-start gap-3">
          <Calendar className="w-5 h-5 text-muted-foreground mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm font-semibold">Next Schedule</p>
            {next_schedule ? (
              <>
                <p className="text-xs text-muted-foreground">{new Date(next_schedule.datetime).toLocaleDateString('en-AU', { weekday: 'short', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</p>
                <p className="text-xs text-muted-foreground capitalize">{next_schedule.type}</p>
              </>
            ) : (
              <p className="text-xs text-muted-foreground">No upcoming schedule</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── KPI Bar ── */
function KpiBar({ dashboard, showFunding }) {
  if (!dashboard) return null;
  const items = [];
  if (showFunding) items.push({ icon: DollarSign, value: `$${(dashboard.funding_remaining || 0).toLocaleString()}`, label: `${getBrandName()} Tracked Remaining`, title: `Tracked internally by ${getBrandName()} from approved services. May not equal the participant's live NDIS plan balance, especially if they use more than one provider.`, color: '' });
  items.push({ icon: Calendar, value: dashboard.active_shifts || 0, label: 'Active Shifts', color: '' });
  items.push({ icon: Users, value: dashboard.assigned_staff_count || 0, label: 'Assigned Staff', color: '' });
  items.push({ icon: AlertTriangle, value: dashboard.active_risks || 0, label: 'Active Risks', color: dashboard.high_critical_risks > 0 ? 'text-red-600' : '' });
  items.push({ icon: Clock, value: dashboard.upcoming_appointments || 0, label: 'Upcoming Appointments', color: '' });

  return (
    <div className="grid grid-cols-5 border rounded-lg divide-x" data-testid="v3-kpi-bar">
      {items.map((item, i) => {
        const Icon = item.icon;
        return (
          <div key={i} className="flex items-center gap-3 px-4 py-3">
            <Icon className={`w-5 h-5 ${item.color || 'text-muted-foreground'} flex-shrink-0`} />
            <div>
              <p className={`text-lg font-bold ${item.color}`}>{item.value}</p>
              <p className="text-[10px] text-muted-foreground leading-tight" title={item.title}>{item.label}</p>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ── Overview Card ── */
function OverviewCard({ icon: Icon, title, children, onEdit, testId }) {
  return (
    <Card data-testid={testId}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Icon className="w-4 h-4 text-muted-foreground" />
            <h3 className="text-sm font-semibold">{title}</h3>
          </div>
          {onEdit && (
            <button onClick={onEdit} className="text-muted-foreground hover:text-foreground" data-testid={`${testId}-edit`}>
              <Pencil className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
        {children}
      </CardContent>
    </Card>
  );
}

/* ── Recent Activity ── */
function RecentActivity({ participantId }) {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);

  useEffect(() => {
    api.get(`/participants/${participantId}/unified-timeline?limit=5`)
      .then(r => setItems(r.data?.data?.items || []))
      .catch(() => {});
  }, [participantId]);

  const TYPE_ICONS = {
    snapshot_update: { icon: User, color: 'text-emerald-600', bg: 'bg-emerald-50' },
    chart: { icon: Activity, color: 'text-sky-600', bg: 'bg-sky-50' },
    note: { icon: FileText, color: 'text-violet-600', bg: 'bg-violet-50' },
    incident: { icon: AlertTriangle, color: 'text-red-600', bg: 'bg-red-50' },
    document: { icon: FileText, color: 'text-amber-600', bg: 'bg-amber-50' },
    careplan: { icon: Target, color: 'text-indigo-600', bg: 'bg-indigo-50' },
  };

  return (
    <div data-testid="v3-recent-activity">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold">Recent Activity</h3>
        <Button variant="link" size="sm" className="h-5 text-xs p-0 text-primary" onClick={() => navigate(`/participants/${participantId}?tab=timeline`)}>
          View all
        </Button>
      </div>
      {items.length === 0 ? (
        <p className="text-xs text-muted-foreground italic py-4">No recent activity</p>
      ) : (
        <div className="space-y-3">
          {items.map((item, idx) => {
            const cfg = TYPE_ICONS[item.type] || TYPE_ICONS.note;
            const Icon = cfg.icon;
            return (
              <div key={item.id || idx} className="flex gap-3" data-testid={`v3-activity-${idx}`}>
                <div className={`w-8 h-8 rounded-full ${cfg.bg} flex items-center justify-center flex-shrink-0 mt-0.5`}>
                  <Icon className={`w-4 h-4 ${cfg.color}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium">{item.title}</p>
                  <p className="text-xs text-muted-foreground line-clamp-1">{item.description}</p>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-[11px] text-muted-foreground">{item.timestamp ? new Date(item.timestamp).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}</p>
                  <p className="text-[10px] text-muted-foreground">{item.created_by?.name}</p>
                </div>
              </div>
            );
          })}
        </div>
      )}
      <div className="mt-4">
        <Button variant="outline" size="sm" className="w-full text-xs" onClick={() => navigate(`/participants/${participantId}?tab=timeline`)} data-testid="v3-view-timeline">
          View full timeline
        </Button>
      </div>
    </div>
  );
}

/* ── Main V3 Layout ── */
export function ParticipantProfileV3Layout({
  participant, participantId, accessControl, dashboard,
  snapshot, overviewProfile, canEditOverview, onEditOverview,
  onEditSnapshot, onRefreshSnapshot
}) {
  const p = participant;
  const op = overviewProfile || {};

  // Data derivation
  const disabilityCondition = op.primary_disability || p.primary_disability || p.disability_type || null;
  const disabilityImpacts = op.disability_impact || [];
  const goalsList = op.goals_aspirations || [];
  const allergiesList = op.allergies || [];
  const familyEntries = (op.family_support_network || []).length > 0 ? op.family_support_network : (() => {
    const fb = [];
    if (p.guardian_name) fb.push({ name: p.guardian_name, relationship: 'Guardian' });
    return fb;
  })();
  const cultural = op.cultural_linguistic_needs || {};
  const hobbiesList = op.hobbies_interests || [];
  const aboutText = op.about || p.bio || p.about || p.participant_notes || null;

  return (
    <div className="space-y-5" data-testid="profile-v3-layout">
      {/* Snapshot Hero */}
      <SnapshotHero snapshot={snapshot} onReview={onEditSnapshot} />

      {/* KPI Bar */}
      <KpiBar dashboard={dashboard} showFunding={accessControl.show_funding_amount} />

      {/* Care Plan & Goals v2 — Phase 1.4: chart integration card.
          Renders only when `care_plan_goals_v2_enabled` is ON. */}
      {dashboard?.care_and_goals && (
        <CareAndGoalsCard careAndGoals={dashboard.care_and_goals} />
      )}

      {/* Overview Content: 3-column grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Left Column */}
        <div className="space-y-4">
          <OverviewCard icon={User} title={`About ${p.preferred_name || p.first_name || ''}`} onEdit={canEditOverview ? onEditOverview : null} testId="v3-about">
            <p className="text-sm text-muted-foreground line-clamp-4">{aboutText || `${p.first_name || ''} is a participant receiving NDIS support.`}</p>
            {aboutText && aboutText.length > 200 && <button className="text-xs text-primary mt-1">Read more</button>}
          </OverviewCard>

          <OverviewCard icon={Target} title="Goals & Aspirations" onEdit={canEditOverview ? onEditOverview : null} testId="v3-goals">
            {goalsList.length > 0 ? (
              <>
                <ul className="space-y-1 text-sm text-muted-foreground">
                  {goalsList.slice(0, 3).map((g, i) => <li key={i} className="flex items-start gap-1.5"><span className="mt-2 w-1 h-1 rounded-full bg-muted-foreground flex-shrink-0" />{g}</li>)}
                </ul>
                {goalsList.length > 3 && <button className="text-xs text-primary mt-2">View all</button>}
              </>
            ) : (
              <p className="text-xs text-muted-foreground italic">No goals recorded</p>
            )}
          </OverviewCard>

          <OverviewCard icon={Users} title="Family & Support Network" onEdit={canEditOverview ? onEditOverview : null} testId="v3-family">
            {familyEntries.length > 0 ? (
              <>
                {familyEntries.slice(0, 2).map((f, i) => (
                  <div key={i} className="text-sm mb-1">
                    <span className="font-medium">{f.name}</span> <span className="text-muted-foreground">({f.relationship})</span>
                    {f.notes && <p className="text-xs text-muted-foreground">{f.notes}</p>}
                  </div>
                ))}
                {familyEntries.length > 2 && <button className="text-xs text-primary mt-1">View all</button>}
              </>
            ) : (
              <p className="text-xs text-muted-foreground italic">No family members recorded</p>
            )}
          </OverviewCard>
        </div>

        {/* Middle Column */}
        <div className="space-y-4">
          <OverviewCard icon={Shield} title="Disability" onEdit={canEditOverview ? onEditOverview : null} testId="v3-disability">
            {disabilityCondition ? (
              <div className="text-sm text-muted-foreground">
                <p className="font-medium text-foreground">{disabilityCondition}</p>
                {disabilityImpacts.length > 0 && (
                  <p className="mt-1">{disabilityImpacts.join('. ')}.</p>
                )}
                {disabilityImpacts.length > 2 && <button className="text-xs text-primary mt-1">Read more</button>}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground italic">No disability details recorded</p>
            )}
          </OverviewCard>

          <OverviewCard icon={AlertTriangle} title="Allergies" onEdit={canEditOverview ? onEditOverview : null} testId="v3-allergies">
            {allergiesList.length > 0 ? (
              <div className="flex gap-1.5 flex-wrap">
                {allergiesList.map((a, i) => (
                  <span key={i} className="text-sm">{a} <Badge className="text-[9px] bg-red-50 text-red-600 border-red-200 ml-0.5">Allergy</Badge></span>
                ))}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground italic">No known allergies</p>
            )}
          </OverviewCard>

          <OverviewCard icon={Globe} title="Cultural & Linguistic Needs" onEdit={canEditOverview ? onEditOverview : null} testId="v3-cultural">
            {cultural.language || p.primary_language ? (
              <div className="text-sm text-muted-foreground">
                <p>Language: <span className="text-foreground">{cultural.language || p.primary_language}</span></p>
                <p>{(cultural.interpreter_required || p.interpreter_required) ? 'Interpreter required' : 'No interpreter required'}</p>
                {cultural.notes && <p className="mt-1">{cultural.notes}</p>}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground italic">No cultural needs recorded</p>
            )}
          </OverviewCard>
        </div>

        {/* Right Column: Recent Activity */}
        <div>
          <Card>
            <CardContent className="p-4">
              <RecentActivity participantId={participantId} />
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Footer info bar */}
      <div className="flex items-center justify-between text-[11px] text-muted-foreground border-t pt-3 mt-2" data-testid="v3-footer">
        <span>Information is based on the latest available data. Please review regularly.</span>
        <div className="flex gap-4">
          <span>Created: {p.created_at ? new Date(p.created_at).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}</span>
          <span>Updated: {p.updated_at ? new Date(p.updated_at).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}</span>
        </div>
      </div>
    </div>
  );
}
